import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LoadingScreen } from "@/components/ProtectedRoute";
import { toast } from "sonner";
import { FileText } from "lucide-react";

export default function Quote() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [form, setForm] = useState({ service: "", address: "", zip: "", yard_size: "Medium (0.25-0.5 acre)", frequency: "one-time", details: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { api.get(`/providers/${id}`).then((r) => { setP(r.data); setForm((f) => ({ ...f, service: r.data.services?.[0]?.category })); }).catch(() => {}); }, [id]);
  if (!p) return <LoadingScreen />;

  const svc = p.services?.find((s) => s.category === form.service) || p.services?.[0];

  const submit = async () => {
    if (!user) { toast.error("Please log in first"); navigate("/login"); return; }
    if (!form.address || !form.zip) { toast.error("Please fill address and ZIP"); return; }
    setSubmitting(true);
    try {
      await api.post("/quotes", {
        provider_id: id, service_key: svc.category, service_name: svc.name,
        address: form.address, zip_code: form.zip, yard_size: form.yard_size,
        frequency: form.frequency, details: form.details,
      });
      toast.success("Quote request sent! The provider will respond soon.");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
      setSubmitting(false);
    }
  };

  return (
    <Layout hideConcierge>
      <div className="max-w-2xl mx-auto px-5 sm:px-8 py-10">
        <div className="flex items-center gap-2 text-lawn"><FileText className="w-5 h-5" /><span className="text-sm font-bold uppercase tracking-wide">Request a Quote</span></div>
        <h1 className="font-display text-3xl font-extrabold text-lawn-ink mt-2">Get a custom quote from {p.business_name}</h1>
        <p className="text-muted-foreground mt-1">Tell us about your yard and we'll get you a price.</p>

        <div className="bg-white border border-border rounded-2xl p-6 mt-8 space-y-4">
          <div>
            <Label>Service</Label>
            <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
              <SelectTrigger data-testid="quote-service" className="mt-1.5 rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent>{p.services?.map((s) => <SelectItem key={s.category} value={s.category}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Yard size</Label>
              <Select value={form.yard_size} onValueChange={(v) => setForm({ ...form, yard_size: v })}>
                <SelectTrigger data-testid="quote-yardsize" className="mt-1.5 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Small (<0.25 acre)">Small (&lt;0.25 acre)</SelectItem>
                  <SelectItem value="Medium (0.25-0.5 acre)">Medium (0.25-0.5 acre)</SelectItem>
                  <SelectItem value="Large (>0.5 acre)">Large (&gt;0.5 acre)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Frequency</Label>
              <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
                <SelectTrigger data-testid="quote-frequency" className="mt-1.5 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="one-time">One-time</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="biweekly">Biweekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>Address</Label><Input data-testid="quote-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="123 Main St" className="mt-1.5 rounded-xl" /></div>
            <div><Label>ZIP</Label><Input data-testid="quote-zip" value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })} placeholder="78701" className="mt-1.5 rounded-xl" /></div>
          </div>
          <div><Label>Details</Label><Textarea data-testid="quote-details" value={form.details} onChange={(e) => setForm({ ...form, details: e.target.value })} placeholder="Describe your yard, any obstacles, goals…" className="mt-1.5 rounded-xl" rows={4} /></div>
          <Button data-testid="quote-submit-btn" onClick={submit} disabled={submitting} className="w-full rounded-full bg-lawn hover:bg-lawn-dark font-semibold h-11">{submitting ? "Sending…" : "Send Quote Request"}</Button>
        </div>
      </div>
    </Layout>
  );
}
